import React, { Fragment } from 'react'
import {TransProps} from '@lingui/react'
import { Dialog, Transition } from '@headlessui/react'
import {
    ExclamationIcon,
    InformationCircleIcon,
    CheckCircleIcon,
    XIcon,
    ExclamationCircleIcon
} from '@heroicons/react/outline'
import {getColor} from 'components/utils'
import {Button} from 'components/UI/Button/Button'


// Type
export type typeConfirmDialogDashboard = {
    variant?: string
    type: 'error' | 'warning' | 'info' | 'success'
    title: React.ReactElement<TransProps>
    message: React.ReactElement<TransProps>
    primaryButton?: {title: React.ReactElement<TransProps>, action: () => void, notDecoration?: boolean}
    secondaryButton?: {title: React.ReactElement<TransProps>, action: () => void}
    openDialog: boolean
    setOpenDialog: React.Dispatch<React.SetStateAction<boolean>>
    notClose?: boolean
}


// Component
export const ConfirmDialogDashboard: React.FC<typeConfirmDialogDashboard> = ({
    variant,
    type,
    title,
    message,
    primaryButton,
    secondaryButton,
    openDialog,
    setOpenDialog,
    notClose
}) => {

    const color = getColor(variant)

    let decorationColor
    switch (type) {
        case "error":
            decorationColor = 'red'
            break
        case "warning":
            decorationColor = 'yellow'
            break
        case "success":
            decorationColor = 'green'
            break
        case "info":
            decorationColor = 'blue'
            break
    }

    return (
        <Transition.Root show={openDialog} as={Fragment}>
            <Dialog as="div" static className="fixed z-20 inset-0 overflow-y-auto" open={openDialog} onClose={notClose? ()=>undefined : setOpenDialog}>
                <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    {/* This element is to trick the browser into centering the modal contents. */}
                    <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
                            {notClose ?
                                null
                                : <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                                <button
                                    type="button"
                                    className={ `bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${ color }-500` }
                                    onClick={ () => setOpenDialog(false) }
                                >
                                    <span className="sr-only">Close</span>
                                    <XIcon className="h-6 w-6" aria-hidden="true"/>
                                </button>
                            </div> }
                            <div className="sm:flex sm:items-start">
                                <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-${decorationColor}-100 sm:mx-0 sm:h-10 sm:w-10`}>

                                    {type === "error" ? <ExclamationIcon className={`h-6 w-6 text-${decorationColor}-600`} aria-hidden="true" /> : null}
                                    {type === "info" ? <InformationCircleIcon className={`h-6 w-6 text-${decorationColor}-600`} aria-hidden="true" /> : null}
                                    {type === "warning" ? <ExclamationCircleIcon className={`h-6 w-6 text-${decorationColor}-600`} aria-hidden="true" /> : null}
                                    {type === "success" ? <CheckCircleIcon className={`h-6 w-6 text-${decorationColor}-600`} aria-hidden="true" /> : null}

                                </div>
                                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                    <Dialog.Title as="h3" className="text-lg leading-6 font-medium text-gray-900">
                                        {title}
                                    </Dialog.Title>
                                    <div className="mt-2">
                                        <p className="text-sm text-gray-500 break-all">
                                            {message}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <div className="mt-5 sm:mt-4 flex gap-x-2 gap-y-2 sm:flex-row-reverse">

                                {primaryButton
                                    ? <button
                                        type="button"
                                        className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-${primaryButton.notDecoration ? color : decorationColor}-600 text-base font-medium text-white hover:bg-${primaryButton.notDecoration ? color : decorationColor}-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-${primaryButton.notDecoration ? color : decorationColor}-500 sm:w-auto sm:text-sm`}
                                        onClick={() => primaryButton.action()}
                                    >
                                        {primaryButton.title}
                                    </button>
                                    : null
                                }

                                {secondaryButton
                                    ? <Button type="button" secondary={true} label={secondaryButton.title} onClick={() => secondaryButton.action()} buttonClassName="w-fill"/>
                                    : null
                                }

                            </div>
                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    )
}
